import { useState } from 'react';
import { Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAppStore } from '@/stores/appStore';
import { SessionList } from '@/components/SessionList';
import { SessionStats } from '@/components/SessionStats';
import { CorpusList } from '@/components/CorpusList';
import { ActionModal } from '@/components/ActionModal';
import { DocumentUpload } from '@/components/DocumentUpload';
import { AgentConfigPanel } from '@/components/AgentConfigPanel';
import { useCreateSession, useCorpus } from '@/hooks/useApi';
import { useNavigate } from 'react-router-dom';
import { useBreakpoint } from '@/hooks/useMediaQuery';
import { cn } from '@/lib/utils';

export function Sidebar() {
  const { ui } = useAppStore();
  const navigate = useNavigate();
  const createSessionMutation = useCreateSession();
  const { data: corpusResponse } = useCorpus();
  const { isTablet } = useBreakpoint();

  // Default to sessions expanded on desktop, collapsed on tablet for space
  const [isAgentConfigCollapsed, setIsAgentConfigCollapsed] = useState(false); // Agent config expanded by default
  const [isSessionsCollapsed, setIsSessionsCollapsed] = useState(isTablet);
  const [isCorpusCollapsed, setIsCorpusCollapsed] = useState(true);
  const [isStatsCollapsed, setIsStatsCollapsed] = useState(true);

  const handleNewChat = () => {
    createSessionMutation.mutate(undefined, {
      onSuccess: (session) => {
        navigate(`/chat/${session.id}`);
      },
    });
  };

  if (!ui.sidebarOpen) return null;

  return (
    <div className="flex h-full w-full flex-col border-r bg-background">
      {/* Header */}
      <div className="p-4 flex-shrink-0">
        <Button
          className="w-full justify-start gap-2"
          size="sm"
          onClick={handleNewChat}
          disabled={createSessionMutation.isPending}
        >
          <Plus className="h-4 w-4" />
          New Chat
        </Button>
      </div>

      <Separator />

      {/* Agent Configuration */}
      <div className="flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsAgentConfigCollapsed(!isAgentConfigCollapsed)}
          className="mx-2 my-1 justify-start gap-2 h-8 px-2 w-auto hover:bg-muted/50"
        >
          {isAgentConfigCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          <span className="text-sm font-medium text-muted-foreground">
            Agent Configuration
          </span>
        </Button>

        {!isAgentConfigCollapsed && (
          <div className="px-2 pb-2">
            <AgentConfigPanel />
          </div>
        )}
      </div>

      <Separator />

      {/* Sessions */}
      <div className={cn(
        "flex flex-col overflow-hidden min-h-0 transition-all duration-200",
        isSessionsCollapsed ? "flex-shrink-0" : "flex-1"
      )}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsSessionsCollapsed(!isSessionsCollapsed)}
          className="mx-2 my-1 justify-start gap-2 h-8 px-2 flex-shrink-0 hover:bg-muted/50"
        >
          {isSessionsCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          <h3 className="text-sm font-medium text-muted-foreground">
            Sessions
          </h3>
        </Button>

        {!isSessionsCollapsed && (
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-2">
                <SessionList />
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      <Separator />

      {/* Corpus */}
      <div className={cn(
        "flex flex-col overflow-hidden min-h-0 transition-all duration-200",
        isCorpusCollapsed ? "flex-shrink-0" : "flex-1"
      )}>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCorpusCollapsed(!isCorpusCollapsed)}
          className="mx-2 my-1 justify-start gap-2 h-8 px-2 flex-shrink-0 hover:bg-muted/50"
        >
          {isCorpusCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          <h3 className="text-sm font-medium text-muted-foreground">
            Corpus
            {corpusResponse?.company?.fileCount !== undefined && (
              <span className="ml-1 text-xs">
                ({corpusResponse.company.fileCount} file{corpusResponse.company.fileCount !== 1 ? 's' : ''})
              </span>
            )}
          </h3>
        </Button>

        {!isCorpusCollapsed && (
          <div className="flex-1 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="px-2">
                <CorpusList />
              </div>
            </ScrollArea>
          </div>
        )}
      </div>

      <Separator />

      {/* Session Stats */}
      <div className="flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsStatsCollapsed(!isStatsCollapsed)}
          className="mx-2 my-1 justify-start gap-2 h-8 px-2 w-auto hover:bg-muted/50"
        >
          {isStatsCollapsed ? (
            <ChevronRight className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
          <span className="text-sm font-medium text-muted-foreground">
            Statistics
          </span>
        </Button>

        {!isStatsCollapsed && (
          <div className="px-2 pb-2">
            <SessionStats />
          </div>
        )}
      </div>

      <Separator />

      {/* Footer */}
      <div className="p-4 flex-shrink-0 space-y-2">
        <DocumentUpload />
        <ActionModal />
      </div>
    </div>
  );
}